import gql from 'graphql-tag';
import {useMemo} from 'react';

import {useUserRunsActivityQuery, useUserRunsQuery} from '../generated/graphql';
import {useCachedData} from '../util/cache';
import {propagateErrorsContext} from '../util/errors';
import {reportsTableFragment} from './../components/ReportsTable';
import {runsTableFragment} from './../components/RunsTable';

export const PROFILE_PAGE_QUERY = gql`
  query ProfilePage($username: String!) {
    user(userName: $username) {
      id
      name
      username
      email
      photoUrl
      userInfo
      hideTeamsFromPublic
      accountType
      defaultEntity {
        id
        name
      }
      views(viewType: "runs", first: 10000) {
        edges {
          node {
            id
            ...ReportsTableFragment
          }
        }
      }
      starredViews(first: 500) {
        edges {
          node {
            id
            ...ReportsTableFragment
          }
        }
      }
      teams(first: 100) {
        edges {
          node {
            id
            name
            photoUrl
            isTeam
          }
        }
      }
    }
  }
  ${reportsTableFragment}
`;

export const PROFILE_PAGE_PROJECTS_QUERY = gql`
  query ProfilePageProjects($username: String!) {
    user(userName: $username) {
      id
      projects(first: 100) {
        edges {
          node {
            id
            name
            entityName
            access
            description
            totalRuns
            lastActive
            runCount
            user {
              id
              username
            }
          }
        }
      }
    }
  }
`;

export const USER_RUNS_ACTIVITY_QUERY = gql`
  query UserRunsActivity($username: String!, $dailyRunCountLimit: Int!) {
    user(userName: $username) {
      id
      dailyRunCount(limit: $dailyRunCountLimit)
    }
  }
`;

export const USER_RUNS_QUERY = gql`
  query UserRuns($username: String!, $pattern: String, $first: Int = 100) {
    user(userName: $username) {
      id
      runs(first: $first, pattern: $pattern) {
        edges {
          node {
            id
            ...RunsTableFragment
          }
        }
      }
    }
  }
  ${runsTableFragment}
`;

export function useCachedUserRunsActivityQuery(
  username: string,
  dailyRunCountLimit: number
) {
  const cacheKey = {
    username,
    dailyRunCountLimit,
  };

  const userRunsActivityQuery = useUserRunsActivityQuery({
    variables: {
      username,
      dailyRunCountLimit,
    },
    context: propagateErrorsContext(),
  });

  const cachedUserRunsActivityQuery = useCachedData<
    typeof userRunsActivityQuery.data
  >({
    namespace: 'UserRunsActivityQuery',
    key: cacheKey,
    dataFromServer: userRunsActivityQuery.data,
    waitingForDataFromServer: userRunsActivityQuery.loading,
  });

  return useMemo(() => {
    if (cachedUserRunsActivityQuery) {
      return {
        data: cachedUserRunsActivityQuery,
        loading: false,
      };
    }
    return userRunsActivityQuery;
  }, [cachedUserRunsActivityQuery, userRunsActivityQuery]);
}

export function useCachedUserRunsQuery(
  username: string,
  pattern?: string,
  first?: number
) {
  const cacheKey = {
    username,
    pattern,
    first,
  };

  const userRunsQuery = useUserRunsQuery({
    variables: cacheKey,
    context: propagateErrorsContext(),
  });

  const cachedUserRunsQuery = useCachedData<typeof userRunsQuery.data>({
    namespace: 'UserRunsQuery',
    key: cacheKey,
    dataFromServer: userRunsQuery.data,
    waitingForDataFromServer: userRunsQuery.loading,
  });

  return useMemo(() => {
    if (cachedUserRunsQuery) {
      return {
        data: cachedUserRunsQuery,
        loading: false,
      };
    }
    return userRunsQuery;
  }, [cachedUserRunsQuery, userRunsQuery]);
}
